import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { Plus, Eye, Edit, Copy, Mail, Filter, ExternalLink, FileText, Search, X, Download } from 'lucide-react';
import { collection, query, orderBy, getDocs, where, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { RouteOffer } from '@/types/routeOffers';
import { formatCurrency, formatDate } from '@/lib/utils';

export default function RouteOffersList() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { toast } = useToast();

  const [offers, setOffers] = useState<RouteOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('draft');
  const [emailSearch, setEmailSearch] = useState<string>('');
  const [lastBookedExport, setLastBookedExport] = useState<Date | null>(() => {
    const stored = localStorage.getItem('sch_booked_last_export');
    return stored ? new Date(stored) : null;
  });
  const [lastUnbookedExport, setLastUnbookedExport] = useState<Date | null>(() => {
    const stored = localStorage.getItem('sch_unbooked_last_export');
    return stored ? new Date(stored) : null;
  });

  // Redirect if not admin
  useEffect(() => {
    if (role && role !== 'admin') {
      navigate('/dashboard');
      toast({
        title: 'Access Denied',
        description: 'You need admin privileges to access this page.',
        variant: 'destructive',
      });
    }
  }, [role, navigate, toast]);

  // Load route offers
  useEffect(() => {
    console.log('[RouteOffersList] useEffect triggered. user:', !!user, 'role:', role);

    if (!user || role !== 'admin') {
      console.log('[RouteOffersList] Skipping load - not authorized');
      return;
    }

    const loadOffers = async () => {
      try {
        console.log('[RouteOffersList] Starting to load offers...');
        setLoading(true);

        let q = query(
          collection(db, 'routeOffers'),
          orderBy('createdAt', 'desc')
        );

        // Filter by status
        console.log('[RouteOffersList] Filtering by status:', filterStatus);
        q = query(q, where('status', '==', filterStatus));

        console.log('[RouteOffersList] Executing Firestore query...');
        const querySnapshot = await getDocs(q);
        console.log('[RouteOffersList] Query completed. Found', querySnapshot.docs.length, 'offers');

        // Helper to convert dates (handles both Timestamp and string formats)
        const toDate = (value: any) => {
          if (!value) return undefined;
          if (typeof value === 'string') return new Date(value);
          if (value.toDate) return value.toDate();
          return value;
        };

        const offersData = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            expiresAt: toDate(data.expiresAt),
            sentAt: toDate(data.sentAt),
            filledAt: toDate(data.filledAt),
            cancelledAt: toDate(data.cancelledAt),
            recipients: data.recipients?.map((r: any) => ({
              ...r,
              viewedAt: toDate(r.viewedAt),
              respondedAt: toDate(r.respondedAt),
              paidAt: toDate(r.paidAt),
              notifiedAt: toDate(r.notifiedAt),
            })) || [],
          } as RouteOffer;
        });

        setOffers(offersData);
        console.log('[RouteOffersList] Offers set successfully');
      } catch (error) {
        console.error('[RouteOffersList] Error loading route offers:', error);
        toast({
          title: 'Error',
          description: 'Failed to load route offers. Please try again.',
          variant: 'destructive',
        });
      } finally {
        console.log('[RouteOffersList] Setting loading to false');
        setLoading(false);
      }
    };

    loadOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, role, filterStatus]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'partially_filled':
        return 'bg-blue-100 text-blue-800';
      case 'filled':
        return 'bg-purple-100 text-purple-800';
      case 'expired':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Open Quote Requests',
      active: 'Offer Sent',
      partially_filled: 'Offer Accepted',
      filled: 'Route Filled',
      expired: 'Expired',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  };

  // Filter offers by email search
  const filteredOffers = React.useMemo(() => {
    if (!emailSearch.trim()) {
      return offers;
    }

    const searchTerm = emailSearch.toLowerCase().trim();
    return offers.filter(offer =>
      offer.recipients?.some(recipient =>
        recipient.email?.toLowerCase().includes(searchTerm)
      )
    );
  }, [offers, emailSearch]);

  const CSV_HEADERS = [
    'Offer ID',
    'Route Name',
    'Offer Status',
    'Pickup Location',
    'Dropoff Location',
    'Pickup Time',
    'Dropoff Time',
    'Weekly Rate',
    'Sent At',
    'Expires At',
    'Recipient Email',
    'Recipient Name',
    'Recipient Status',
    'Students',
    'Selected Weeks',
    'Subtotal',
    'Total Amount',
    'Paid At',
    'Enrollment ID',
    'Quote ID',
  ];

  const buildOfferRows = (offer: RouteOffer): string[][] => {
    const recipients = offer.recipients?.length ? offer.recipients : [null];
    return recipients.map(recipient => [
      offer.id,
      offer.routeName,
      getStatusLabel(offer.status),
      offer.pickupLocation || '',
      offer.dropoffLocation || '',
      offer.pickupTime || '',
      offer.dropoffTime || '',
      offer.adjustedWeeklyRate?.toString() ?? '',
      offer.sentAt ? formatDate(offer.sentAt) : '',
      offer.expiresAt ? formatDate(offer.expiresAt) : '',
      recipient?.email ?? '',
      recipient?.name ?? '',
      recipient?.status ?? '',
      recipient?.studentCount?.toString() ?? '',
      recipient?.selectedWeeks?.join(', ') ?? '',
      recipient?.subtotal?.toString() ?? '',
      recipient?.totalAmount?.toString() ?? '',
      recipient?.paidAt ? formatDate(recipient.paidAt) : '',
      recipient?.enrollmentId ?? '',
      recipient?.quoteId ?? '',
    ]);
  };

  const downloadCSV = (rows: string[][], filename: string) => {
    const csvContent = [CSV_HEADERS, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportToCSV = () => {
    const rows = filteredOffers.flatMap(offer => buildOfferRows(offer));
    downloadCSV(rows, `offers-${filterStatus}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportAllBooked = async (sinceDate?: Date) => {
    try {
      toast({ title: 'Exporting...', description: sinceDate ? `Loading booked customers updated since ${sinceDate.toLocaleDateString()}.` : 'Loading all booked customers.' });

      const toDate = (value: any) => {
        if (!value) return undefined;
        if (typeof value === 'string') return new Date(value);
        if (value.toDate) return value.toDate();
        return value;
      };

      // Query the enrollments collection — authoritative source for paid customers
      const snapshot = await getDocs(
        sinceDate
          ? query(collection(db, 'enrollments'), where('updatedAt', '>=', sinceDate), orderBy('updatedAt', 'desc'))
          : query(collection(db, 'enrollments'), orderBy('createdAt', 'desc'))
      );

      const headers = [
        'Enrollment ID',
        'Route Offer ID',
        'Route Name',
        'Pickup Location',
        'Dropoff Location',
        'Pickup Time',
        'Dropoff Time',
        'Guardian Name',
        'Guardian Email',
        'Guardian Phone',
        'Student Count',
        'Student Details (Grade / Car Seat / Booster / Assistance)',
        'Car Seats Needed',
        'Boosters Needed',
        'Assistance Needed',
        'Pickup Address (Submitted)',
        'Nearest Hubstop',
        'Enrolled Weeks',
        'Weekly Rate Per Student',
        'Subtotal',
        'Referral Discount',
        'Total Amount',
        'Payment Status',
        'Paid At',
        'Enrollment Status',
        'Timing & Pickup Preferences',
        'Additional Notes / Message',
        'Interested in Late Shuttle',
        'Created At',
      ];

      // Fetch all enrollments then join quoteRequests for student detail
      const rows = await Promise.all(snapshot.docs.map(async (enrollDoc) => {
        const e = enrollDoc.data();
        let students: any[] = [];
        let pickupAddressSubmitted = '';
        let hubstopName = '';
        let timingPreference = '';
        let additionalMessage = '';
        let interestedInLateShuttle = '';

        if (e.quoteId) {
          try {
            const qrSnap = await getDoc(doc(db, 'quoteRequests', e.quoteId));
            if (qrSnap.exists()) {
              const qr = qrSnap.data();
              students = qr.students ?? [];
              pickupAddressSubmitted = qr.pickupAddress ?? '';
              hubstopName = qr.selectedHubstop ?? '';
              timingPreference = qr.timingPreference ?? '';
              additionalMessage = qr.message ?? '';
              interestedInLateShuttle = qr.interestedInLateShuttle ? 'Yes' : 'No';
            }
          } catch (_) { /* skip if lookup fails */ }
        }

        const studentDetails = students.map((s: any, i: number) => {
          const flags = [
            s.needsCarSeat && 'Car Seat',
            s.needsBooster && 'Booster',
            s.needsAssistance && 'Assistance',
          ].filter(Boolean).join('+') || 'None';
          return `Camper ${i + 1}: Grade ${s.grade || '?'} [${flags}]`;
        }).join(' | ');

        return [
          enrollDoc.id,
          e.routeOfferId ?? '',
          e.routeName ?? '',
          e.pickupLocation ?? '',
          e.dropoffLocation ?? '',
          e.pickupTime ?? '',
          e.dropoffTime ?? '',
          e.guardianName ?? '',
          e.guardianEmail ?? '',
          e.guardianPhone ?? '',
          (e.studentCount ?? students.length ?? '').toString(),
          studentDetails,
          students.filter((s: any) => s.needsCarSeat).length.toString(),
          students.filter((s: any) => s.needsBooster).length.toString(),
          students.filter((s: any) => s.needsAssistance).length.toString(),
          pickupAddressSubmitted,
          hubstopName,
          (e.enrolledWeeks ?? []).join(', '),
          (e.weeklyRatePerStudent ?? '').toString(),
          (e.subtotal ?? '').toString(),
          (e.referralDiscount ?? '').toString(),
          (e.totalAmount ?? '').toString(),
          e.paymentStatus ?? '',
          e.paidAt ? formatDate(toDate(e.paidAt)) : '',
          e.status ?? '',
          timingPreference,
          additionalMessage,
          interestedInLateShuttle,
          e.createdAt ? formatDate(toDate(e.createdAt)) : '',
        ];
      }));

      if (rows.length === 0) {
        toast({ title: 'No data', description: 'No enrollments found.' });
        return;
      }

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = sinceDate ? `-since-${sinceDate.toISOString().slice(0, 10)}` : '';
      link.download = `booked-customers${suffix}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      const now = new Date();
      localStorage.setItem('sch_booked_last_export', now.toISOString());
      setLastBookedExport(now);

      toast({ title: 'Export complete', description: `${rows.length} booked customer(s) exported.` });
    } catch (error) {
      console.error('Export error:', error);
      toast({ title: 'Export failed', description: 'Could not load booked customers.', variant: 'destructive' });
    }
  };

  const exportAllUnbooked = async (sinceDate?: Date) => {
    try {
      toast({ title: 'Exporting...', description: sinceDate ? `Loading inquiries updated since ${sinceDate.toLocaleDateString()}.` : 'Loading all unbooked inquiries.' });

      const toDate = (value: any) => {
        if (!value) return undefined;
        if (typeof value === 'string') return new Date(value);
        if (value.toDate) return value.toDate();
        return value;
      };

      // Query quoteRequests — the authoritative source for all inquiry detail
      const snapshot = await getDocs(
        sinceDate
          ? query(collection(db, 'quoteRequests'), where('updatedAt', '>=', sinceDate), orderBy('updatedAt', 'desc'))
          : query(collection(db, 'quoteRequests'), orderBy('createdAt', 'desc'))
      );

      const unbookedHeaders = [
        'Quote Request ID',
        'Guardian Name',
        'Guardian Email',
        'Guardian Phone',
        'Student Count',
        'Student Details (Grade / Car Seat / Booster / Assistance)',
        'Car Seats Needed',
        'Boosters Needed',
        'Assistance Needed',
        'Pickup Address',
        'Nearest Hubstop',
        'Selected Weeks',
        'Week Count',
        'Price Per Student Per Week',
        'Total Price',
        'Interested in Late Shuttle',
        'Timing & Pickup Preferences',
        'Additional Notes / Message',
        'Status',
        'Submitted At',
      ];

      const rows: string[][] = snapshot.docs
        .filter(d => {
          const status = d.data().status ?? '';
          return status !== 'enrolled' && status !== 'paid';
        })
        .map(d => {
          const q = d.data();
          const students: any[] = q.students ?? [];
          const gi = q.guardianInfo ?? {};

          const studentDetails = students.map((s: any, i: number) => {
            const flags = [
              s.needsCarSeat && 'Car Seat',
              s.needsBooster && 'Booster',
              s.needsAssistance && 'Assistance',
            ].filter(Boolean).join('+') || 'None';
            return `Camper ${i + 1}: Grade ${s.grade || '?'} [${flags}]`;
          }).join(' | ');

          return [
            d.id,
            gi.fullName ?? q.guardianName ?? '',
            gi.email ?? q.guardianEmail ?? '',
            gi.phone ?? q.guardianPhone ?? '',
            students.length.toString(),
            studentDetails,
            students.filter((s: any) => s.needsCarSeat).length.toString(),
            students.filter((s: any) => s.needsBooster).length.toString(),
            students.filter((s: any) => s.needsAssistance).length.toString(),
            q.pickupAddress ?? '',
            q.selectedHubstop ?? '',
            (q.selectedWeeks ?? []).join(', '),
            (q.selectedWeeks ?? []).length.toString(),
            (q.pricing?.perWeekWithDiscount ?? q.pricing?.perWeekPerStudent ?? '').toString(),
            (q.pricing?.totalPrice ?? '').toString(),
            q.interestedInLateShuttle ? 'Yes' : 'No',
            q.timingPreference ?? '',
            q.message ?? '',
            q.status ?? '',
            q.createdAt ? formatDate(toDate(q.createdAt)) : '',
          ];
        });

      if (rows.length === 0) {
        toast({ title: 'No data', description: 'No unbooked inquiries found.' });
        return;
      }

      const csvContent = [unbookedHeaders, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = sinceDate ? `-since-${sinceDate.toISOString().slice(0, 10)}` : '';
      link.download = `unbooked-families${suffix}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      const now = new Date();
      localStorage.setItem('sch_unbooked_last_export', now.toISOString());
      setLastUnbookedExport(now);

      toast({ title: 'Export complete', description: `${rows.length} unbooked inquir(ies) exported.` });
    } catch (error) {
      console.error('Export error:', error);
      toast({ title: 'Export failed', description: 'Could not load unbooked families.', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading route offers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Route Offers</h1>
            <p className="text-gray-600 mt-1">
              Manage and send transportation offers to customers
            </p>
          </div>
          <Button
            onClick={() => navigate('/admin/route-offers/create')}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Offer
          </Button>
        </div>

        {/* Export Panel */}
        <Card className="mb-6 border-shuttlebee-yellow border">
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Export Customer Lists</h2>
            <div className="flex flex-wrap gap-3">
              {/* Booked */}
              <div className="flex-1 min-w-[240px]">
                <div className="rounded-lg border border-green-200 bg-green-50 overflow-hidden">
                  <button
                    onClick={() => exportAllBooked(lastBookedExport ?? undefined)}
                    className="w-full flex items-start gap-3 p-3 hover:bg-green-100 transition-colors text-left"
                  >
                    <Download className="w-5 h-5 text-green-700 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-green-900 text-sm">Paid / Booked Customers</div>
                      <div className="text-xs text-green-700 mt-0.5">
                        {lastBookedExport
                          ? <>New/updated since {lastBookedExport.toLocaleDateString()} {lastBookedExport.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                          : 'Everyone who has completed payment'}
                      </div>
                    </div>
                  </button>
                  {lastBookedExport && (
                    <div className="border-t border-green-200 px-3 py-1.5 flex items-center justify-between">
                      <span className="text-xs text-green-600">Last pulled: {lastBookedExport.toLocaleDateString()}</span>
                      <button
                        onClick={() => exportAllBooked(undefined)}
                        className="text-xs text-green-700 underline hover:text-green-900"
                      >
                        Download all
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {/* Unbooked */}
              <div className="flex-1 min-w-[240px]">
                <div className="rounded-lg border border-blue-200 bg-blue-50 overflow-hidden">
                  <button
                    onClick={() => exportAllUnbooked(lastUnbookedExport ?? undefined)}
                    className="w-full flex items-start gap-3 p-3 hover:bg-blue-100 transition-colors text-left"
                  >
                    <Download className="w-5 h-5 text-blue-700 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-blue-900 text-sm">Interested / Not Yet Booked</div>
                      <div className="text-xs text-blue-700 mt-0.5">
                        {lastUnbookedExport
                          ? <>New/updated since {lastUnbookedExport.toLocaleDateString()} {lastUnbookedExport.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                          : 'Everyone who expressed interest but has not completed payment'}
                      </div>
                    </div>
                  </button>
                  {lastUnbookedExport && (
                    <div className="border-t border-blue-200 px-3 py-1.5 flex items-center justify-between">
                      <span className="text-xs text-blue-600">Last pulled: {lastUnbookedExport.toLocaleDateString()}</span>
                      <button
                        onClick={() => exportAllUnbooked(undefined)}
                        className="text-xs text-blue-700 underline hover:text-blue-900"
                      >
                        Download all
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Banner for Open Quote Requests */}
        {filterStatus === 'draft' && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900">Open Quote Requests</h3>
                <p className="text-sm text-blue-700 mt-1">
                  These draft offers are automatically created when customers submit quote requests.
                  Review the details, adjust pricing if needed, and send offers to customers.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Email Search */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Search className="w-5 h-5 text-gray-500" />
              <div className="flex-1 relative">
                <input
                  type="email"
                  placeholder="Search by recipient email address..."
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent pr-10"
                />
                {emailSearch && (
                  <button
                    onClick={() => setEmailSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
              {emailSearch && (
                <div className="text-sm text-gray-600 whitespace-nowrap">
                  {filteredOffers.length} result{filteredOffers.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-gray-500" />
              <div className="flex gap-2 flex-wrap">
                {['draft', 'active', 'partially_filled', 'filled', 'expired', 'cancelled'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      filterStatus === status
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {getStatusLabel(status)}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Offers List */}
        {filteredOffers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-gray-400 mb-4">
                {emailSearch ? <Search className="w-16 h-16 mx-auto" /> : <Mail className="w-16 h-16 mx-auto" />}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {emailSearch ? 'No matching offers found' : 'No route offers yet'}
              </h3>
              <p className="text-gray-600 mb-6">
                {emailSearch
                  ? `No offers found with recipient email containing "${emailSearch}". Try a different search term.`
                  : 'Create your first route offer to start sending transportation offers to customers.'}
              </p>
              {emailSearch ? (
                <Button
                  onClick={() => setEmailSearch('')}
                  variant="outline"
                  className="flex items-center gap-2 mx-auto"
                >
                  <X className="w-4 h-4" />
                  Clear Search
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/admin/route-offers/create')}
                  className="flex items-center gap-2 mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  Create First Offer
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredOffers.map((offer) => (
              <Card key={offer.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {offer.routeName}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(offer.status)}`}>
                          {getStatusLabel(offer.status)}
                        </span>
                        {offer.sourceQuoteId && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            From Quote Request
                          </span>
                        )}
                      </div>

                      <div className="mb-2">
                        <span className="text-xs text-gray-500 font-mono">
                          ID: {offer.id}
                        </span>
                      </div>

                      {offer.sourceQuoteId && (
                        <div className="mb-3">
                          <Link
                            to={`/admin/quote-requests/${offer.sourceQuoteId}`}
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Original Quote Request
                          </Link>
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-4">
                        <div>
                          <span className="font-medium">Capacity:</span>{' '}
                          {offer.currentEnrollment}/{offer.maxStudents} students
                        </div>
                        <div>
                          <span className="font-medium">Recipients:</span>{' '}
                          {offer.totalRecipientsCount} ({offer.activeRecipientsCount} active)
                        </div>
                        <div>
                          <span className="font-medium">Rate:</span>{' '}
                          {formatCurrency(offer.adjustedWeeklyRate)}/week
                        </div>
                        <div>
                          <span className="font-medium">Expires:</span>{' '}
                          {formatDate(offer.expiresAt)}
                        </div>
                      </div>

                      <div className="text-sm text-gray-500">
                        <span className="font-medium">Route:</span> {offer.pickupLocation} → {offer.dropoffLocation}
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/route-offers/${offer.id}`)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/route-offers/${offer.id}/edit`)}
                        className="flex items-center gap-1"
                      >
                        <Mail className="w-4 h-4" />
                        Send Offer
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // TODO: Implement duplicate functionality
                          toast({
                            title: 'Coming Soon',
                            description: 'Duplicate offer feature will be available soon.',
                          });
                        }}
                        className="flex items-center gap-1"
                      >
                        <Copy className="w-4 h-4" />
                        Duplicate
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
